import type { ReactElement } from "react";
import { ShieldAlert } from "lucide-react";
import {
  composeDisclaimer,
  disclaimerFor,
  rulesetStamp,
  type DisclaimerContext,
} from "@/tax";

/**
 * <AtoDisclaimer /> — the mandatory disclaimer surface for any tax-adjacent
 * output BuildIntel produces.
 *
 * Why this exists alongside the older `TaxDisclaimer.jsx`:
 *  - Strong types on the `context` prop force every caller to declare which
 *    surface they're rendering into — no more accidental "generic" wording
 *    on a BAS number.
 *  - Composes with the currently active ATO ruleset (see
 *    `@/tax/rulesets`), so the tradie sees a version stamp — this is what
 *    makes the audit trail portable to `TaxGuidanceLog`.
 *  - React 18-safe, `data-testid` first-class for the test harness.
 *
 * Migration policy:
 *  - New tax-adjacent screens: use this component.
 *  - Existing screens using `TaxDisclaimer.jsx`: keep working — we'll
 *    opportunistically migrate them next time we touch each file.
 */

export interface AtoDisclaimerProps {
  /** Which UI surface — drives the wording. Required by design. */
  context: DisclaimerContext;
  /** Shorter blurb (inline near a CTA). Defaults to the full sentence. */
  compact?: boolean;
  /** Override the default testid. Handy for pages that render two. */
  testId?: string;
  /** Optional className tacked onto the outer wrapper for layout tweaks. */
  className?: string;
}

export function AtoDisclaimer({
  context,
  compact = false,
  testId = "ato-disclaimer",
  className = "",
}: AtoDisclaimerProps): ReactElement {
  const copy = disclaimerFor(context);
  const stamp = rulesetStamp();
  const text = compact ? copy.short : copy.full;

  if (compact) {
    return (
      <div
        data-testid={testId}
        data-context={context}
        data-ruleset={stamp}
        className={`text-[10px] text-white/50 inline-flex items-start gap-1 leading-snug ${className}`}
      >
        <ShieldAlert className="w-3 h-3 mt-0.5 shrink-0 text-white/40" />
        <span>{text}</span>
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      data-context={context}
      data-ruleset={stamp}
      className={`rounded-lg border border-white/5 bg-white/[0.03] p-2.5 text-[11px] text-white/70 leading-snug flex items-start gap-2 ${className}`}
    >
      <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#FBBF24]" />
      <div className="min-w-0">
        <div>{text}</div>
        <div
          data-testid={`${testId}-stamp`}
          className="text-[10px] text-white/40 mt-1"
        >
          {stamp}
        </div>
      </div>
    </div>
  );
}

/**
 * Non-JSX escape hatch — for exports, PDFs, `navigator.share()`, or any
 * plain-string surface. Same wording as the component, just rendered as a
 * one-line sentence.
 */
export function atoDisclaimerText(
  context: DisclaimerContext,
  variant: "short" | "full" = "full"
): string {
  return composeDisclaimer(context, variant);
}

export default AtoDisclaimer;
